// src/services/SensorService.js

/**
 * Service to simulate real-time IoT sensor data (Digital Twin).
 * Generates realistic fluctuations for Soil Moisture, pH, and NPK levels.
 */

// Target "Ideal" values to fluctuate around
// Tuned for DMV Region (Maryland Coastal Plain - Sandy Loam)
const TARGETS = {
    MOISTURE: 62, // % (Maryland springs are wet)
    PH: 6.2,      // pH scale (Naturally acidic in DMV, needs lime monitoring)
    NITROGEN: 42, // mg/kg
    PHOSPHORUS: 38, // mg/kg
    POTASSIUM: 165, // mg/kg
    TEMP: 18      // Celsius (Seasonal avg)
};

class SensorService {
    constructor() {
        // Map of fieldId -> sensorData
        this.fieldData = new Map();
        this.listeners = [];

        // Start the simulation loop - UPDATE EVERY 5 MINUTES
        this.interval = setInterval(() => this.updateSimulation(), 300000);
    }

    /**
     * Get or initialize data for a specific field
     */
    getOrCreateFieldData(fieldId = 'default') {
        if (!this.fieldData.has(fieldId)) {
            // Seed initial values based on fieldId to make them distinct
            const seed = typeof fieldId === 'number' ? fieldId : 0;
            this.fieldData.set(fieldId, {
                moisture: this.walk(TARGETS.MOISTURE, 5, 45, 85, 1, seed),
                ph: this.walk(TARGETS.PH, 0.5, 5.8, 7.2, 2, seed + 1),
                nitrogen: this.walk(TARGETS.NITROGEN, 10, 30, 80, 1, seed + 2),
                phosphorus: this.walk(TARGETS.PHOSPHORUS, 5, 20, 60, 1, seed + 3),
                potassium: this.walk(TARGETS.POTASSIUM, 20, 120, 220, 1, seed + 4),
                temperature: this.walk(TARGETS.TEMP, 2, 10, 35, 1, seed + 5),
                lastUpdated: new Date().toLocaleTimeString()
            });
        }
        return this.fieldData.get(fieldId);
    }

    /**
     * Update simulation for all tracked fields
     */
    updateSimulation() {
        for (const [fieldId, data] of this.fieldData.entries()) {
            this.fieldData.set(fieldId, {
                moisture: this.walk(data.moisture, 0.2, 40, 90, 1),
                ph: this.walk(data.ph, 0.01, 5.8, 7.2, 2),
                nitrogen: this.walk(data.nitrogen, 0.5, 30, 80, 1),
                phosphorus: this.walk(data.phosphorus, 0.3, 20, 60, 1),
                potassium: this.walk(data.potassium, 0.5, 120, 220, 1),
                temperature: this.walk(data.temperature, 0.1, 10, 35, 1),
                lastUpdated: new Date().toLocaleTimeString()
            });
        }

        this.notify();
    }

    /**
     * Random walk helper
     */
    walk(val, step, min, max, decimals = 0, seedOffset = 0) {
        // If seedOffset is provided (initialization), we use it to jump from target
        const randomSource = seedOffset ? (Math.sin(seedOffset) + 1) / 2 : Math.random();
        let change = (randomSource * step * 2) - step;
        let newVal = val + change;

        if (newVal < min) newVal = min + (Math.random() * step);
        if (newVal > max) newVal = max - (Math.random() * step);

        return parseFloat(newVal.toFixed(decimals));
    }

    /**
     * Subscribe to sensor updates for a specific field
     */
    subscribe(callback, fieldId = 'default') {
        const listener = { callback, fieldId };
        this.listeners.push(listener);

        // Initial push
        callback(this.getOrCreateFieldData(fieldId));

        return () => {
            this.listeners = this.listeners.filter(l => l !== listener);
        };
    }

    notify() {
        this.listeners.forEach(({ callback, fieldId }) => {
            callback(this.getOrCreateFieldData(fieldId));
        });
    }

    /**
     * Manual refresh trigger
     */
    refresh(fieldId) {
        const data = this.getOrCreateFieldData(fieldId);
        // Add a tiny bit of jitter to show it's "live"
        const newData = {
            ...data,
            moisture: this.walk(data.moisture, 0.1, 40, 90, 1),
            lastUpdated: new Date().toLocaleTimeString()
        };
        this.fieldData.set(fieldId, newData);
        this.notify();
        return newData;
    }

    /**
     * Get readings (legacy support or specific field)
     */
    getReadings(fieldId = 'default') {
        const data = this.getOrCreateFieldData(fieldId);
        return [
            { id: 'moisture', name: 'Soil Moisture', value: data.moisture, unit: '%', status: this.getStatus('moisture', data.moisture) },
            { id: 'ph', name: 'Soil pH', value: data.ph, unit: 'pH', status: this.getStatus('ph', data.ph) },
            { id: 'n', name: 'Nitrogen (N)', value: data.nitrogen, unit: 'mg/kg', status: this.getStatus('n', data.nitrogen) },
            { id: 'p', name: 'Phosphorus (P)', value: data.phosphorus, unit: 'mg/kg', status: this.getStatus('p', data.phosphorus) },
            { id: 'k', name: 'Potassium (K)', value: data.potassium, unit: 'mg/kg', status: this.getStatus('k', data.potassium) },
            { id: 'temp', name: 'Soil Temp', value: data.temperature, unit: '°C', status: 'optimal' },
        ];
    }

    getStatus(type, val) {
        if (type === 'moisture') return (val < 45 || val > 85) ? 'warning' : 'optimal';
        if (type === 'ph') return (val < 5.8 || val > 7.2) ? 'warning' : 'optimal';
        if (type === 'n') return val < 35 ? 'critical' : 'optimal';
        return 'optimal';
    }
}

export const sensorService = new SensorService();
